import { type IconType } from '@assembly-js/design-system';
import React from 'react';
import { makeStyles } from 'tss-react/mui';

import {
  FILES_PAGE,
  FORMS_PAGE,
  HELPDESK_PAGE,
  MESSAGES_PAGE,
  PAYMENTS_PAGE,
} from 'src/constants';
import { PortalConfigContext } from 'src/context';
import { LayoutPreview } from 'src/legacy/components/LayoutPreview';
import { SidebarItem } from 'src/legacy/components/LayoutPreview/layoutPreviewTypes';
import BaseTypography from 'src/legacy/components/Text/BaseTypography';
import { GraySmall, white } from 'src/theme/colors';
import ColorUtils from 'src/utils/ColorUtils';

interface ExtensionModuleContentPreviewProps {
  embedContent: string;
  extensionName: string;
  extensionIcon: IconType;
}

interface StyleProps {
  sidebarBackgroundColor: string;
  sidebarTextColor: string;
}

const useStyles = makeStyles<StyleProps>()((theme, props) => ({
  root: {
    display: 'flex',
    justifyContent: 'center',
    flexDirection: 'column',
    alignItems: 'center',
    marginTop: theme.spacing(2),
    paddingBottom: theme.spacing(12.5),
  },
  clientPreviewText: {
    letterSpacing: '2px',
    color: GraySmall,
    marginBottom: theme.spacing(1.5),
  },
  layoutContainer: {
    width: 800,
    height: 500,
  },
  layoutContent: {
    width: '-webkit-fill-available',
  },
  sidebar: {
    minWidth: 111,
    width: 111,
    borderBottomLeftRadius: theme.shape.borderRadius,
    backgroundColor: props.sidebarBackgroundColor,
  },
  layoutBody: {
    height: '100%',
    overflowX: 'auto',
    maxHeight: '400px',
    maxWidth: '650px',
  },
  iFrameContainer: {
    height: 'calc(100% - 58px)',
    position: 'relative',
  },
  sidebarItem: {
    color: props.sidebarTextColor,
  },
  sidebarItemActive: {
    backgroundColor: ColorUtils.GetColorDarknessShades(
      props.sidebarBackgroundColor,
    ).midDark,
    color: props.sidebarTextColor,
  },
}));

const sidebarDefaultItems = {
  MESSAGES: {
    title: MESSAGES_PAGE.label,
    icon: MESSAGES_PAGE.icon,
    iconName: 'messages',
  },
  FILES: {
    title: FILES_PAGE.label,
    icon: FILES_PAGE.icon,
    iconName: 'File',
  },
  FORMS: {
    title: FORMS_PAGE.label,
    icon: FORMS_PAGE.icon,
    iconName: 'Form',
  },
  BILLING: {
    title: PAYMENTS_PAGE.label,
    icon: PAYMENTS_PAGE.icon,
    iconName: 'payments',
  },
  KNOWLEDGE_BASE: {
    title: HELPDESK_PAGE.label,
    icon: HELPDESK_PAGE.icon,
    iconName: 'Helpdesk',
  },
};
export const ExtensionModuleContentPreview: React.FC<
  ExtensionModuleContentPreviewProps
> = ({ embedContent, extensionName, extensionIcon }) => {
  const portalConfig = React.useContext(PortalConfigContext);
  const { clientSidebarBackgroundColor, clientSidebarTextColor } =
    portalConfig.brand;

  // when advanced branding is turned on
  // and we have "clientSidebarBackgroundColor" available, use "clientSidebarBackgroundColor"
  // as sidebar background color
  // else use theme primary color
  const sidebarBackgroundColor =
    clientSidebarBackgroundColor || portalConfig.theme.palette.primary.main;

  // when advanced branding is turned on
  // and we have "clientSidebarTextColor" available use "clientSidebarTextColor"
  // as sidebar text color
  // else use white
  const sidebarTextColor = clientSidebarTextColor || white;

  const { classes } = useStyles({ sidebarBackgroundColor, sidebarTextColor });

  const sidebarItems: Array<SidebarItem> = React.useMemo(
    () => [
      sidebarDefaultItems.MESSAGES,
      sidebarDefaultItems.FILES,
      sidebarDefaultItems.FORMS,
      sidebarDefaultItems.BILLING,
      sidebarDefaultItems.KNOWLEDGE_BASE,
      {
        title: extensionName,
        active: true,
        iconName: extensionIcon,
      },
    ],
    [extensionIcon, extensionName],
  );
  return (
    <div className={classes.root}>
      <BaseTypography
        className={classes.clientPreviewText}
        fontType="15Regular"
      >
        Client preview
      </BaseTypography>
      <LayoutPreview
        classes={{
          layoutContainer: classes.layoutContainer,
          layoutContent: classes.layoutContent,
          layoutBody: classes.layoutBody,
          sidebar: classes.sidebar,
          item: classes.sidebarItem,
          itemActive: classes.sidebarItemActive,
        }}
        logoProps={{
          portalNamePreviewColor: sidebarTextColor,
        }}
        items={sidebarItems}
      >
        <div
          className={classes.iFrameContainer}
          dangerouslySetInnerHTML={{
            __html: embedContent,
          }}
        />
      </LayoutPreview>
    </div>
  );
};
